import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { PosApiService, PosAuthService } from '@adminpages/pos-api';

export type RepairStatus =
  | 'received'
  | 'diagnosing'
  | 'quoted'
  | 'authorized'
  | 'in_repair'
  | 'waiting_parts'
  | 'ready'
  | 'delivered'
  | 'cancelled';

export interface RepairClient {
  id: number;
  uuid?: string;
  name: string;
  phone?: string;
  email?: string;
}

export interface RepairQuote {
  uuid: string;
  folio: string;
  type?: string;
  version?: number;
  status: string;
  paymentStatus: string;
  validUntil?: string;
  total: number;
  paidTotal: number;
  balanceDue: number;
  items: Array<{ id: number; name: string; quantity: number; unitPrice: number; total: number }>;
  payments: Array<{ uuid: string; type: string; method: string; amount: number; status: string; reference?: string }>;
}

export interface RepairAttachment {
  uuid: string;
  type: string;
  title: string;
  mimeType?: string;
  visibility: 'internal' | 'customer';
  includeInDocuments: boolean;
  capturedStatus?: string;
  createdAt?: string;
}

export interface RepairHistoryItem {
  fromStatus?: string;
  toStatus: string;
  statusLabel: string;
  message?: string;
  notes?: string;
  user?: string;
  changedAt?: string;
}

export interface RepairOrder {
  uuid: string;
  folio: string;
  status: RepairStatus;
  statusLabel: string;
  priority: 'normal' | 'urgent' | 'warranty';
  entryType: 'standard' | 'warranty';
  assetType: string;
  brand?: string;
  model?: string;
  serialNumber?: string;
  color?: string;
  reportedIssue: string;
  physicalCondition?: string;
  accessoriesReceived?: string;
  diagnosis?: string;
  resolution?: string;
  receivedAt?: string;
  readyAt?: string;
  deliveredAt?: string;
  warrantyExpiresAt?: string;
  client: RepairClient;
  assignedUser?: string;
  total: number;
  paidTotal: number;
  balanceDue: number;
  quotes: RepairQuote[];
  attachments: RepairAttachment[];
  history: RepairHistoryItem[];
  availableTransitions: RepairStatus[];
}

export interface CreateRepairPayload {
  client_id: number;
  priority?: 'normal' | 'urgent';
  asset_type: string;
  brand?: string;
  model?: string;
  serial_number?: string;
  color?: string;
  access_credentials?: string;
  reported_issue: string;
  physical_condition?: string;
  accessories_received?: string;
  notes?: string;
}

export interface RepairPaymentPayload {
  commercial_order_uuid: string;
  method: 'cash' | 'transfer';
  amount: number;
  reference?: string;
  notes?: string;
}

interface ApiEnvelope<T> {
  data: T;
}

type ApiRecord = Record<string, unknown>;

@Injectable({ providedIn: 'root' })
export class RepairsApiService {
  private readonly http = inject(HttpClient);
  private readonly auth = inject(PosAuthService);
  private readonly posApi = inject(PosApiService);

  async repairs(workspaceId: number, params: { q?: string; status?: RepairStatus; perPage?: number } = {}): Promise<RepairOrder[]> {
    let query = new HttpParams().set('per_page', String(params.perPage ?? 50));

    if (params.q) query = query.set('q', params.q);
    if (params.status) query = query.set('status', params.status);

    const response = await firstValueFrom(
      this.http.get<ApiEnvelope<ApiRecord[]>>(this.baseUrl(), {
        headers: this.auth.headers(workspaceId),
        params: query,
      }),
    );

    return response.data.map(mapRepair);
  }

  async repair(workspaceId: number, uuid: string): Promise<RepairOrder> {
    const response = await firstValueFrom(
      this.http.get<ApiEnvelope<ApiRecord>>(`${this.baseUrl()}/${encodeURIComponent(uuid)}`, {
        headers: this.auth.headers(workspaceId),
      }),
    );

    return mapRepair(response.data);
  }

  async create(workspaceId: number, payload: CreateRepairPayload): Promise<RepairOrder> {
    const response = await firstValueFrom(
      this.http.post<ApiEnvelope<ApiRecord>>(this.baseUrl(), payload, {
        headers: this.auth.headers(workspaceId),
      }),
    );

    return mapRepair(response.data);
  }

  async updateTechnicalData(workspaceId: number, uuid: string, payload: Record<string, unknown>): Promise<RepairOrder> {
    const response = await firstValueFrom(
      this.http.patch<ApiEnvelope<ApiRecord>>(`${this.baseUrl()}/${encodeURIComponent(uuid)}/technical-data`, payload, {
        headers: this.auth.headers(workspaceId),
      }),
    );

    return mapRepair(response.data);
  }

  async transition(workspaceId: number, uuid: string, status: RepairStatus, payload: Record<string, unknown> = {}): Promise<RepairOrder> {
    const response = await firstValueFrom(
      this.http.post<ApiEnvelope<ApiRecord>>(`${this.baseUrl()}/${encodeURIComponent(uuid)}/transition`, { status, ...payload }, {
        headers: this.auth.headers(workspaceId),
      }),
    );

    return mapRepair(response.data);
  }

  async addPayment(workspaceId: number, uuid: string, payload: RepairPaymentPayload): Promise<RepairOrder> {
    const response = await firstValueFrom(
      this.http.post<ApiEnvelope<ApiRecord>>(`${this.baseUrl()}/${encodeURIComponent(uuid)}/payments`, payload, {
        headers: this.auth.headers(workspaceId),
      }),
    );

    return mapRepair(response.data);
  }

  async uploadAttachment(
    workspaceId: number,
    uuid: string,
    file: File,
    data: { type: string; visibility: 'internal' | 'customer'; includeInDocuments: boolean; title?: string },
  ): Promise<RepairAttachment> {
    const form = new FormData();
    form.append('file', file);
    form.append('type', data.type);
    form.append('visibility', data.visibility);
    form.append('include_in_documents', data.includeInDocuments ? '1' : '0');
    if (data.title) form.append('title', data.title);

    const response = await firstValueFrom(
      this.http.post<ApiEnvelope<ApiRecord>>(`${this.baseUrl()}/${encodeURIComponent(uuid)}/attachments`, form, {
        headers: this.auth.headers(workspaceId),
      }),
    );

    return mapAttachment(response.data);
  }

  private baseUrl(): string {
    if (typeof window === 'undefined') return '/api/v1/repairs';

    const configured = window.localStorage.getItem('adminpages.repairs.apiBaseUrl');
    if (configured) return configured.replace(/\/$/, '');

    return this.posApi.apiBaseUrl().replace(/\/pos\/?$/, '/repairs');
  }
}

export function mapRepair(data: ApiRecord): RepairOrder {
  return {
    uuid: stringValue(data['uuid']),
    folio: stringValue(data['folio']),
    status: repairStatus(data['status']),
    statusLabel: stringValue(data['status_label']),
    priority: priorityValue(data['priority']),
    entryType: data['entry_type'] === 'warranty' ? 'warranty' : 'standard',
    assetType: stringValue(data['asset_type']),
    brand: optionalString(data['brand']),
    model: optionalString(data['model']),
    serialNumber: optionalString(data['serial_number']),
    color: optionalString(data['color']),
    reportedIssue: stringValue(data['reported_issue']),
    physicalCondition: optionalString(data['physical_condition']),
    accessoriesReceived: optionalString(data['accessories_received']),
    diagnosis: optionalString(data['diagnosis']),
    resolution: optionalString(data['resolution']),
    receivedAt: optionalString(data['received_at']),
    readyAt: optionalString(data['ready_at']),
    deliveredAt: optionalString(data['delivered_at']),
    warrantyExpiresAt: optionalString(data['warranty_expires_at']),
    client: mapClient(recordValue(data['client'])),
    assignedUser: optionalString(data['assigned_user']),
    total: numberValue(data['total']),
    paidTotal: numberValue(data['paid_total']),
    balanceDue: numberValue(data['balance_due']),
    quotes: arrayValue(data['quotes']).map((item) => mapQuote(recordValue(item))),
    attachments: arrayValue(data['attachments']).map((item) => mapAttachment(recordValue(item))),
    history: arrayValue(data['history']).map((item) => mapHistory(recordValue(item))),
    availableTransitions: arrayValue(data['available_transitions']).map(repairStatus),
  };
}

function mapClient(data: ApiRecord): RepairClient {
  return {
    id: numberValue(data['id']),
    uuid: optionalString(data['uuid']),
    name: stringValue(data['name']),
    phone: optionalString(data['phone']),
    email: optionalString(data['email']),
  };
}

function mapQuote(data: ApiRecord): RepairQuote {
  return {
    uuid: stringValue(data['uuid']),
    folio: stringValue(data['folio']),
    type: optionalString(data['type']),
    version: optionalNumber(data['version']),
    status: stringValue(data['status']),
    paymentStatus: stringValue(data['payment_status']),
    validUntil: optionalString(data['valid_until']),
    total: numberValue(data['total']),
    paidTotal: numberValue(data['paid_total']),
    balanceDue: numberValue(data['balance_due']),
    items: arrayValue(data['items']).map((item) => {
      const row = recordValue(item);
      return { id: numberValue(row['id']), name: stringValue(row['name']), quantity: numberValue(row['quantity']), unitPrice: numberValue(row['unit_price']), total: numberValue(row['total']) };
    }),
    payments: arrayValue(data['payments']).map((item) => {
      const row = recordValue(item);
      return { uuid: stringValue(row['uuid']), type: stringValue(row['type']), method: stringValue(row['method']), amount: numberValue(row['amount']), status: stringValue(row['status']), reference: optionalString(row['reference']) };
    }),
  };
}

function mapAttachment(data: ApiRecord): RepairAttachment {
  return {
    uuid: stringValue(data['uuid']),
    type: stringValue(data['type']),
    title: stringValue(data['title'], 'Archivo'),
    mimeType: optionalString(data['mime_type']),
    visibility: data['visibility'] === 'customer' ? 'customer' : 'internal',
    includeInDocuments: Boolean(data['include_in_documents']),
    capturedStatus: optionalString(data['captured_status']),
    createdAt: optionalString(data['created_at']),
  };
}

function mapHistory(data: ApiRecord): RepairHistoryItem {
  return {
    fromStatus: optionalString(data['from_status']),
    toStatus: stringValue(data['to_status']),
    statusLabel: stringValue(data['status_label']),
    message: optionalString(data['message']),
    notes: optionalString(data['notes']),
    user: optionalString(data['user']),
    changedAt: optionalString(data['changed_at']),
  };
}

function repairStatus(value: unknown): RepairStatus {
  const statuses: RepairStatus[] = ['received', 'diagnosing', 'quoted', 'authorized', 'in_repair', 'waiting_parts', 'ready', 'delivered', 'cancelled'];
  return statuses.includes(value as RepairStatus) ? value as RepairStatus : 'received';
}

function priorityValue(value: unknown): RepairOrder['priority'] {
  return value === 'urgent' || value === 'warranty' ? value : 'normal';
}

function recordValue(value: unknown): ApiRecord {
  return value && typeof value === 'object' && !Array.isArray(value) ? value as ApiRecord : {};
}

function arrayValue(value: unknown): unknown[] {
  return Array.isArray(value) ? value : [];
}

function stringValue(value: unknown, fallback = ''): string {
  return typeof value === 'string' && value.length > 0 ? value : fallback;
}

function optionalString(value: unknown): string | undefined {
  return typeof value === 'string' && value.length > 0 ? value : undefined;
}

function numberValue(value: unknown): number {
  const result = Number(value);
  return Number.isFinite(result) ? result : 0;
}

function optionalNumber(value: unknown): number | undefined {
  const result = Number(value);
  return Number.isFinite(result) ? result : undefined;
}
